import type { Environment } from '../../../types/config';
import { getUrlsForEnvironment } from '../../../environment';
import type { AuthenticatedApiService } from '../AuthenticatedApi';
import { MetricService } from '../MetricService';

import { SendingPool } from './SendingPool';

type BixelClientOptions = {
  api: AuthenticatedApiService;
  env: Environment;
  clientId: string;
  pool?: SendingPool;
};

type NotifyEmailSent = {
  emailAddress: string;
  emailId: string;
  recipientName: string;
  recipients: Record<'email' | 'name', string>[];
  subject: string;
  urls?: string[];
};

export class Client {
  protected options: BixelClientOptions;
  protected pool: SendingPool;

  constructor(options: BixelClientOptions) {
    this.options = options;

    this.pool = this.options.pool ?? new SendingPool({ env: this.options.env });
  }

  get sendingPool() {
    return this.pool;
  }

  public loadSendingPool(): Promise<void> {
    if (this.pool.persisted) {
      return Promise.resolve();
    }

    return this.getAndSaveSendingPool();
  }

  async notifyEmailSent(request: NotifyEmailSent): Promise<void> {
    const [clientId, emailId, emailAddress] = this.encodeURIComponents([
      request.emailId,
      request.emailAddress,
    ]);

    const requestURL = `v1/generate/sendEmail/${clientId}/${emailId}/${emailAddress}`;
    const bixelEndpoint = this.getEndpoint() + requestURL;
    const payload = {
      subject: request.subject,
      recipientName: request.recipientName,
      recipients: JSON.stringify(request.recipients),
      platform: 'Outlook365',
      vidReplyEnabled: true,
      urls: request.urls ?? [],
      bixelEndpoint,
    };

    const result = await this.sendRequest('POST', '/notifySendEmail', payload);

    if (!result) {
      throw new Error('Failed to notify Bixel of email');
    }
  }

  protected getEndpoint() {
    let url = this.pool.getPoolUrl();
    if (url.substr(-1) !== '/') {
      url = `${url}/`;
    }

    return url;
  }

  protected async getAndSaveSendingPool(): Promise<void> {
    const url = await this.getSendingPoolURL();
    if (!url) {
      throw new Error(`Sending pool url is invalid: ${typeof url}`);
    }

    this.pool.setPool(url);
    this.pool.save();
  }

  protected getSendingPoolURL(): Promise<string> {
    return this.sendAppRequest('GET', '/v2/accounts/bixel');
  }

  protected encodeURIComponents(components: string[]): string[] {
    return [encodeURIComponent(this.options.clientId), ...components.map((c) => encodeURIComponent(c))];
  }

  protected sendAppRequest(method: 'GET', appEndpoint: string): Promise<string> {
    const urls = getUrlsForEnvironment(this.options.env);
    const requestUrl = `${urls.apiUrl}${appEndpoint}`;
    return this.sendRequest(method, requestUrl);
  }

  protected sendBixelRequest(method: 'POST', requestURL: string, payload: unknown): Promise<string> {
    const bixelEndpoint = this.getEndpoint() + requestURL;
    return this.sendRequest(method, bixelEndpoint, payload);
  }

  protected async sendRequest(
    method: 'GET' | 'POST',
    requestURL: string,
    payload: unknown = null,
  ): Promise<string> {
    const abortController = new AbortController();
    const extraInit = { signal: abortController.signal };
    const timeout = setTimeout(() => abortController.abort(), 10000);

    let response: Response | null;
    if (method === 'GET') {
      response = await this.options.api.get(requestURL, extraInit);
    } else {
      const body = JSON.stringify(payload);
      response = await this.options.api.post(requestURL, body, extraInit);
    }

    clearTimeout(timeout);
    if (response) {
      return await response.text();
    }

    MetricService.yardstick('fetch.bixel.failure');
    return '';
  }
}
